<template>
  <div class="flex flex-col">
    <AppModal
      v-model="showFormModal"
      :title="editingFlag ? 'Edit feature flag' : 'Create feature flag'"
      hide-footer
      size="md"
    >
      <form class="space-y-4" @submit.prevent="saveFlag">
        <div>
          <label class="text-xs font-semibold text-gray-600 uppercase tracking-wide">Name</label>
          <input
            v-model="form.name"
            type="text"
            autocomplete="off"
            placeholder="e.g. ENABLE_NEW_CHECKOUT"
            class="w-full mt-1 px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary bg-gray-50 font-mono"
            :disabled="saving || !!editingFlag"
            required
          />
          <p v-if="editingFlag" class="text-xs text-gray-500 mt-1">
            Flag name cannot be changed after creation.
          </p>
        </div>
        <div>
          <label class="text-xs font-semibold text-gray-600 uppercase tracking-wide">Status</label>
          <select
            v-model="form.status"
            class="w-full mt-1 px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary bg-white"
            :disabled="saving"
            required
          >
            <option v-for="status in FEATURE_FLAG_STATUSES" :key="status" :value="status">
              {{ status === "ENABLED" ? "Enabled" : "Disabled" }}
            </option>
          </select>
        </div>
        <div>
          <label class="text-xs font-semibold text-gray-600 uppercase tracking-wide">Description</label>
          <textarea
            v-model="form.description"
            rows="2"
            placeholder="Optional description of what this flag controls"
            class="w-full mt-1 px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary bg-gray-50 resize-y"
            :disabled="saving"
          />
        </div>
        <div>
          <label class="text-xs font-semibold text-gray-600 uppercase tracking-wide">Endpoints</label>
          <textarea
            v-model="form.endpointsText"
            rows="3"
            placeholder="One endpoint per line (optional)"
            class="w-full mt-1 px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary bg-gray-50 font-mono resize-y"
            :disabled="saving"
          />
          <p class="text-xs text-gray-500 mt-1">Leave blank if the flag is not tied to specific endpoints.</p>
        </div>
        <div class="flex flex-wrap justify-end gap-2 pt-1">
          <button type="button" class="btn" :disabled="saving" @click="closeFormModal">Cancel</button>
          <button type="submit" class="btn-primary" :disabled="saving || !canSave">
            <i v-if="saving" class="fa fa-spinner fa-spin"></i>
            <i v-else class="fa-solid fa-save"></i>
            <span>{{ editingFlag ? "Update flag" : "Create flag" }}</span>
          </button>
        </div>
      </form>
    </AppModal>

    <div class="flex flex-wrap items-center justify-between gap-3 mb-4">
      <p class="text-sm text-gray-600 flex-1 min-w-0">
        Create and manage feature flags used to enable or disable product behaviour without a redeploy.
      </p>
      <div class="flex flex-wrap items-center gap-2 shrink-0">
        <button type="button" class="btn" :disabled="loading" @click="loadFlags">
          <i class="fa-solid fa-rotate-right" :class="{ 'fa-spin': loading }"></i>
          <span>Refresh</span>
        </button>
        <button type="button" class="btn-primary" @click="openCreateModal">
          <i class="fa-solid fa-plus"></i>
          <span>Create flag</span>
        </button>
      </div>
    </div>

    <div class="flex flex-wrap items-center gap-3 mb-3">
      <input
        v-model="search"
        type="text"
        placeholder="Search by name or description"
        class="px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary w-64"
      />
      <select
        v-model="statusFilter"
        class="px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary bg-white"
      >
        <option value="">All statuses</option>
        <option value="ENABLED">Enabled</option>
        <option value="DISABLED">Disabled</option>
      </select>
    </div>

    <div class="relative min-h-[320px]">
      <div
        v-if="loading && !loaded"
        class="absolute inset-0 z-10 flex flex-col items-center justify-center rounded border border-gray-200 bg-white/90"
      >
        <i class="fa-solid fa-circle-notch fa-spin text-3xl text-primary"></i>
        <p class="mt-3 text-sm text-gray-500">Loading feature flags...</p>
      </div>

      <div :class="{ invisible: loading && !loaded }">
        <div class="bg-white rounded border border-gray-200 overflow-hidden">
          <div class="overflow-x-auto">
            <table class="w-full text-xs">
              <thead class="bg-gray-50 sticky top-0">
                <tr>
                  <th class="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200 w-12">#</th>
                  <th class="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200">Name</th>
                  <th class="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200 w-28">Status</th>
                  <th class="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200">Description</th>
                  <th class="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200 w-24">Endpoints</th>
                  <th class="text-center py-3 px-4 font-semibold text-gray-700 border-b border-gray-200 w-56">Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(flag, idx) in paginatedItems"
                  :key="flag.name"
                  class="hover:bg-gray-50 transition"
                >
                  <td class="py-3 px-4 border-b border-gray-200">{{ rowNumber(idx) }}</td>
                  <td class="py-3 px-4 border-b border-gray-200 font-mono font-medium break-all">
                    {{ flag.name }}
                  </td>
                  <td class="py-3 px-4 border-b border-gray-200">
                    <span
                      class="px-2 py-0.5 rounded-full text-xs font-semibold"
                      :class="flag.status === 'ENABLED' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'"
                    >
                      {{ flag.status === "ENABLED" ? "Enabled" : "Disabled" }}
                    </span>
                  </td>
                  <td class="py-3 px-4 border-b border-gray-200 max-w-[16rem]">
                    <p class="truncate" :title="flag.metadata.description || ''">
                      {{ flag.metadata.description || "—" }}
                    </p>
                  </td>
                  <td class="py-3 px-4 border-b border-gray-200 whitespace-nowrap">
                    {{ flag.metadata.endpoints.length }}
                  </td>
                  <td class="py-3 px-4 border-b border-gray-200">
                    <div class="flex flex-wrap items-center justify-center gap-1">
                      <button
                        type="button"
                        class="btn-sm-primary"
                        :disabled="togglingName === flag.name || deletingName === flag.name"
                        @click="toggleStatus(flag)"
                      >
                        <i
                          v-if="togglingName === flag.name"
                          class="fa fa-spinner fa-spin"
                        ></i>
                        <i
                          v-else
                          :class="flag.status === 'ENABLED' ? 'fa-solid fa-toggle-on' : 'fa-solid fa-toggle-off'"
                        ></i>
                        <span>{{ flag.status === "ENABLED" ? "Disable" : "Enable" }}</span>
                      </button>
                      <button
                        type="button"
                        class="btn-sm-primary"
                        :disabled="!!togglingName || !!deletingName"
                        @click="openEditModal(flag)"
                      >
                        <i class="fa-solid fa-pen"></i>
                        <span>Edit</span>
                      </button>
                      <button
                        type="button"
                        class="btn-sm-danger"
                        :disabled="deletingName === flag.name || togglingName === flag.name"
                        @click="confirmDelete(flag)"
                      >
                        <i v-if="deletingName === flag.name" class="fa fa-spinner fa-spin"></i>
                        <i v-else class="fa-solid fa-trash"></i>
                        <span>Delete</span>
                      </button>
                    </div>
                  </td>
                </tr>
                <tr v-if="loaded && !loading && filteredFlags.length === 0">
                  <td colspan="6" class="py-10 px-4 text-center text-gray-500">
                    <div class="flex flex-col items-center gap-2">
                      <span class="flex h-10 w-10 items-center justify-center rounded-full bg-gray-50 text-gray-400">
                        <i class="fa-solid fa-flag text-lg"></i>
                      </span>
                      <p class="text-sm font-medium text-gray-700">No feature flags found</p>
                      <p class="text-xs text-gray-500">
                        {{
                          flags.length === 0
                            ? 'Click "Create flag" to add the first one.'
                            : "Try changing the search or status filter."
                        }}
                      </p>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <GatewayTablePagination
          :current-page="currentPage"
          :total-pages="totalPages"
          :total-elements="totalElements"
          :page-size="pageSize"
          :range-start="rangeStart"
          :range-end="rangeEnd"
          @previous="previous"
          @next="next"
          @update:page-size="onPageSizeUpdate"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";
import AppModal from "@/components/ui/AppModal.vue";
import GatewayTablePagination from "../gateway/GatewayTablePagination.vue";
import { useClientPagination } from "../gateway/useClientPagination";
import { useConfirmDialog, useToast } from "@/composables";
import {
  FEATURE_FLAG_STATUSES,
  createFeatureFlag,
  deleteFeatureFlag,
  extractErrorMessage,
  fetchFeatureFlags,
  updateFeatureFlag,
  type FeatureFlag,
  type FeatureFlagStatus,
} from "./featureFlagsApi";

const { successToast, errorToast, warningToast } = useToast();
const { showConfirm } = useConfirmDialog();

const flags = ref<FeatureFlag[]>([]);
const loading = ref(false);
const loaded = ref(false);
const saving = ref(false);
const togglingName = ref<string | null>(null);
const deletingName = ref<string | null>(null);
const showFormModal = ref(false);
const editingFlag = ref<FeatureFlag | null>(null);
const search = ref("");
const statusFilter = ref("");
const form = ref({
  name: "",
  status: "DISABLED" as FeatureFlagStatus,
  description: "",
  endpointsText: "",
});

const filteredFlags = computed(() => {
  const query = search.value.trim().toLowerCase();
  return flags.value.filter((flag) => {
    if (statusFilter.value && flag.status !== statusFilter.value) return false;
    if (!query) return true;
    const description = (flag.metadata.description || "").toLowerCase();
    return flag.name.toLowerCase().includes(query) || description.includes(query);
  });
});

const {
  currentPage,
  pageSize,
  totalElements,
  totalPages,
  paginatedItems,
  rangeStart,
  rangeEnd,
  previous,
  next,
  resetPage,
} = useClientPagination(filteredFlags, 25);

const canSave = computed(() => form.value.name.trim() !== "" && !!form.value.status);

const rowNumber = (idx: number) => (currentPage.value - 1) * pageSize.value + idx + 1;

const onPageSizeUpdate = (size: number) => {
  pageSize.value = size;
  resetPage();
};

watch([search, statusFilter], () => resetPage());

const parseEndpoints = (text: string) =>
  text
    .split(/\r?\n|,/)
    .map((item) => item.trim())
    .filter(Boolean);

const resetForm = () => {
  form.value = {
    name: "",
    status: "DISABLED",
    description: "",
    endpointsText: "",
  };
};

const openCreateModal = () => {
  editingFlag.value = null;
  resetForm();
  showFormModal.value = true;
};

const openEditModal = (flag: FeatureFlag) => {
  editingFlag.value = flag;
  form.value = {
    name: flag.name,
    status: flag.status,
    description: flag.metadata.description || "",
    endpointsText: flag.metadata.endpoints.join("\n"),
  };
  showFormModal.value = true;
};

const closeFormModal = () => {
  showFormModal.value = false;
  editingFlag.value = null;
  resetForm();
};

const buildMetadata = () => ({
  description: form.value.description.trim() || null,
  endpoints: parseEndpoints(form.value.endpointsText),
});

const saveFlag = async () => {
  if (!canSave.value) {
    warningToast("Validation", "Enter a flag name");
    return;
  }
  saving.value = true;
  try {
    const metadata = buildMetadata();
    if (editingFlag.value) {
      await updateFeatureFlag(editingFlag.value.name, {
        status: form.value.status,
        metadata,
      });
      successToast("Success", "Feature flag updated");
    } else {
      await createFeatureFlag({
        name: form.value.name.trim(),
        status: form.value.status,
        metadata,
      });
      successToast("Success", "Feature flag created");
    }
    closeFormModal();
    await loadFlags();
  } catch (error: unknown) {
    errorToast(
      "Error",
      extractErrorMessage(
        error,
        editingFlag.value ? "Could not update feature flag" : "Could not create feature flag"
      )
    );
  } finally {
    saving.value = false;
  }
};

const toggleStatus = async (flag: FeatureFlag) => {
  const nextStatus: FeatureFlagStatus = flag.status === "ENABLED" ? "DISABLED" : "ENABLED";
  const confirmed = await showConfirm(
    `${nextStatus === "ENABLED" ? "Enable" : "Disable"} feature flag "${flag.name}"?`,
    {
      title: `${nextStatus === "ENABLED" ? "Enable" : "Disable"} Feature Flag`,
      variant: "warning",
      okTitle: nextStatus === "ENABLED" ? "Enable" : "Disable",
      cancelTitle: "Cancel",
    }
  );
  if (!confirmed) return;

  togglingName.value = flag.name;
  try {
    await updateFeatureFlag(flag.name, {
      status: nextStatus,
      metadata: flag.metadata,
    });
    successToast("Success", `Feature flag ${nextStatus === "ENABLED" ? "enabled" : "disabled"}`);
    await loadFlags();
  } catch (error: unknown) {
    errorToast("Error", extractErrorMessage(error, "Could not update feature flag status"));
  } finally {
    togglingName.value = null;
  }
};

const confirmDelete = async (flag: FeatureFlag) => {
  const confirmed = await showConfirm(
    `Delete feature flag "${flag.name}"? This cannot be undone.`,
    {
      title: "Delete Feature Flag",
      variant: "danger",
      okTitle: "Delete",
      cancelTitle: "Cancel",
    }
  );
  if (!confirmed) return;

  deletingName.value = flag.name;
  try {
    await deleteFeatureFlag(flag.name);
    successToast("Success", "Feature flag deleted");
    await loadFlags();
  } catch (error: unknown) {
    errorToast("Error", extractErrorMessage(error, "Could not delete feature flag"));
  } finally {
    deletingName.value = null;
  }
};

const loadFlags = async () => {
  loading.value = true;
  try {
    flags.value = await fetchFeatureFlags();
    resetPage();
  } catch (error: unknown) {
    flags.value = [];
    resetPage();
    errorToast("Error", extractErrorMessage(error, "Could not load feature flags"));
  } finally {
    loading.value = false;
    loaded.value = true;
  }
};

onMounted(() => {
  loadFlags();
});

defineExpose({ refresh: loadFlags });
</script>
